import {
  Card,
  CardContent,
  CircularProgress,
  Slide,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ECampaignSurveyType } from "pages/dashboard/CampaignDetails/questions/LeftSidebar/constants";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SurveyTemplate, { ESurveyTypes } from "shared/components/SurveyTemplate";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import { EMultipleConfigType } from "store/enums/campaignDetails";
import { ERequestStatus } from "store/enums/index.enum";
import {
  CreateCustomer,
  GetPreviousQuestion,
  GetQuestionConfiguration,
  GetQuestionDetails,
  selectQuestion,
  SetQuestionFinished,
  SkipQuestion,
  SubmitAnswer,
} from "store/slicers/surveyPreview";
import { ESurveyPreviewTypes } from "./constants";
import { metricConfigable } from "pages/dashboard/CampaignDetails";
import { createSurveyTheme } from "theme/idBankTemplate";
import { ThemeProvider } from "@mui/material/styles";
import Not_available from "assets/icons/not_available.svg";
import Completed from "assets/images/completed_survey.png";
import { LStorage } from "store/config/constants";
import { GetTranslationsByLangId } from "store/slicers/translations";
import { selectCampaignInfo } from "store/slicers/campaignDetail";

const ErrMessage = "bk_error_feedback_not_found";

const SurveyPreview = () => {
  const [status, setStatus] = useState("");
  const [isLoading, setLoading] = useState(true);
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState("");

  const windowSize = useRef([window.innerWidth, window.innerHeight]);
  const campaignInfo = useSelector(selectCampaignInfo);

  const methods = useForm({
    defaultValues: {
      answerIDs: [],
      comment: "",
      singleChoice: "",
      contact: "",
      rate: 0,
      contactConfig: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
    },
  });

  const questionData = useSelector(selectQuestion);

  const { details, config } = questionData;
  const navigate = useNavigate();
  const dispatch = useAsyncDispatch();
  const { hash, type } = useParams();

  const templateColorSet = useMemo(() => {
    return {
      mainColor: questionData?.details?.template?.answerColor,
      buttonColor: questionData?.details?.template?.buttonColor,
    };
  }, [
    questionData?.details?.template?.answerColor,
    questionData?.details?.template?.buttonColor,
  ]);

  const generateCustomer = useCallback(async () => {
    const { meta, payload } = await dispatch(
      CreateCustomer({
        hash,
        languageID: campaignInfo?.defaultLanguageID || 34,
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      setStatus("Կներեք, հարցումը հասանելի չէ:");
      return;
    }

    navigate(
      `/${ESurveyPreviewTypes.PERSONAL}/${payload.hash}${location.search}`,
      {
        replace: true,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, hash]);

  const getQuestionData = useCallback(
    async (formData) => {
      await dispatch(GetQuestionDetails(formData));
      setTimeout(() => {
        setLoading(false);
      }, 300);
    },
    [dispatch]
  );

  const surveyStatus = useMemo(() => {
    if (config?.isExpired) {
      setStatus("Ձեր հարցումը ժամկետանց է");
      return false;
    } else if (config?.isFinished) {
      setStatus("Հարգելի հաճախորդ, Դուք արդեն մասնակցել եք հարցմանը");
      return false;
    } else if (errorMessage) {
      setStatus("Հարցումը չի գտնվել");
    } else if (!details && !isLoading) {
      setStatus("Կներեք, հարցումը հասանելի չէ:");
      return false;
    }

    if (details) {
      return true;
    }
  }, [config?.isExpired, config?.isFinished, details, errorMessage, isLoading]);

  const theme = useMemo(() => {
    if (templateColorSet && surveyStatus) {
      return createSurveyTheme(templateColorSet);
    }
  }, [surveyStatus, templateColorSet]);

  const answerIDs = useWatch({
    control: methods.control,
    name: "answerIDs",
  });

  const singleChoiceVal = useWatch({
    control: methods.control,
    name: "singleChoice",
  });

  const commentValue = useWatch({
    control: methods.control,
    name: "comment",
  });

  const checkDisabled = useMemo(() => {
    if (metricConfigable.includes(Number(details?.type))) {
      return !answerIDs.length;
    }
    if (details?.type === Number(ECampaignSurveyType.SingleChoice)) {
      return !singleChoiceVal;
    }

    if (details?.type === Number(ECampaignSurveyType.Comment)) {
      const val = !commentValue;
      return val;
    }
    if (details?.type === Number(ECampaignSurveyType.MultipleChoice)) {
      const checkedAnswersCount = answerIDs.filter((i) => i).length;
      if (details?.multipleConfig?.multipleType === EMultipleConfigType.EXACT) {
        return (
          checkedAnswersCount !== Number(details?.multipleConfig.multipleExact)
        );
      } else if (
        details?.multipleConfig?.multipleType === EMultipleConfigType.RANGE
      ) {
        return !(
          checkedAnswersCount >= Number(details?.multipleConfig.multipleMin) &&
          checkedAnswersCount <= Number(details?.multipleConfig.multipleMax)
        );
      }
      return !checkedAnswersCount;
    }
  }, [
    details?.type,
    details?.multipleConfig,
    answerIDs,
    singleChoiceVal,
    commentValue,
  ]);

  const getQuestionConfig = useCallback(
    async (hash: string) => {
      const { meta, payload, error } = await dispatch(
        GetQuestionConfiguration(hash)
      );
      if (error?.status === 404 && error?.data?.error?.message === ErrMessage) {
        setErrorMessage("SURVEY_404");
        return;
      }
      if (
        meta.requestStatus !== ERequestStatus.FULFILLED ||
        payload.isFinished
      ) {
        return;
      }

      getQuestionData({
        hash,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, getQuestionData]
  );

  const getAnswerRequestData = (formData) => {
    if (
      details?.type === Number(ECampaignSurveyType.SingleChoice) 
    ) {
      return {
        answerIDs: [formData.singleChoice],
        hash,
        surveyID: details.id,
      };
    }
    if (details?.type === Number(ECampaignSurveyType.MultipleChoice)) {
      const answerIDs = details.answers
        .filter((i, index) => formData.answerIDs[index])
        .map((i) => i.id);

      return {
        answerIDs,
        hash,
        surveyID: details.id,
      };
    } else if (details?.type === Number(ECampaignSurveyType.Comment)) {
      return {
        comment: formData.comment,
        answerIDs: details?.answers?.map((i) => i.id),
        hash,
        surveyID: details.id,
      };
    } else if (
      details?.type === Number(ECampaignSurveyType.ContactInformation)
    ) {
      const { firstName, lastName, phone, email } = formData.contactConfig;
      return {
        comment: `${firstName} ${lastName} ${phone} ${email}`,
        answerIDs: details?.answers?.map((i) => i.id),
        hash,
        surveyID: details.id,
      };
    } else {
      return {
        answerIDs: formData.answerIDs,
        hash,
        surveyID: details.id,
      };
    }
  };

  const handleSkip = async () => {
    const { meta } = await dispatch(
      SkipQuestion({
        answerIDs: [],
        hash,
        surveyID: String(details.id),
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    getQuestionData({
      answerIDs: [],
      hash,
      surveyID: details.id,
    });

    methods.reset({
      answerIDs: [],
    });
  };

  const handlePrev = async () => {
    setLoading(true);
     await dispatch(
      GetPreviousQuestion({
        hash,
        surveyID: String(details.id),
        answerIDs: [],
      })
    );

  


    methods.reset({
      answerIDs: [],
    });
    setLoading(false);
  };

  const finishSurvey = useCallback(async () => {
    const { meta } = await dispatch(SetQuestionFinished({ hash }));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }
  }, [dispatch, hash]);

  const handleNext = async (formData) => {
    setLoading(true);
    let requestData: any = {
      answerIDs: [],
    };

    if (details?.type !== Number(ECampaignSurveyType.Welcome)) {
      requestData = getAnswerRequestData(formData);
      const { meta } = await dispatch(SubmitAnswer(requestData));

      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }
    }

    getQuestionData({
      answerIDs: requestData?.answerIDs,
      hash,
      surveyID: details.id,
    });

    methods.reset({
      answerIDs: [],
    });
  };

  const init = useCallback(async () => {
    const activeLang = Number(
      localStorage.getItem(LStorage.PREFERRED_LANGUAGE)
    );

    await dispatch(GetTranslationsByLangId(activeLang || 2));
    if (!type || !hash) {
      return;
    }
    if (type === ESurveyPreviewTypes.GENERAL) {
      generateCustomer();
    } else {
      getQuestionConfig(hash);
    }
  }, [dispatch, type, hash, generateCustomer, getQuestionConfig]);

  useLayoutEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (
      details?.isLast &&
      !config.isFinished &&
      type !== ESurveyPreviewTypes.GENERAL
    ) {
      finishSurvey();
    }
  }, [config?.isFinished, details?.isLast, finishSurvey, type]);

  useEffect(() => {
    setTimeout(() => {
      window.history.forward();
    }, 0);
    window.onunload = function () {
      return null;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (questionData.details?.publicURL) {
      localStorage.setItem("publicURL", questionData.details.publicURL);
    }
  }, [questionData.details?.publicURL]);

  return (
    <Box
      display="flex"
      fontFamily="Roboto"
      justifyContent={"center"}
      sx={{
        backgroundImage: `url(${require("assets/images/bg.png")})`,
        height: windowSize.current[1],
        backgroundSize: "cover",
      }}
    >
      {surveyStatus ? (
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              display: { xs: "flex" },
              alignItems: { xs: "center" },
              margin: { xs: "16px 0", sm: 2 },
            }}
          >
            <Card
              sx={{
                height: { xs: `${windowSize.current[1] * 0.95}px` },
                backgroundColor: "rgb(255 255 255 / 97%)",
                position: "relative",
                overflowY: "scroll",
                overscrollBehavior: "none",
              }}
            >
              <CardContent
                sx={{
                  py: { xs: "10px", lg: "16px" },
                  px: 0,
                  ":last-child": {
                    pb: 0,
                  },
                }}
              >
                <Slide in={!isLoading} direction={isLoading ? "down" : "up"}>
                  <Box
                    sx={{
                      width: {
                        xs: `${windowSize.current[0] * 0.95}px`,
                        sm: `${windowSize.current[0] * 0.8}px`,
                        md: `${windowSize.current[0] * 0.6}px`,
                        lg: `${windowSize.current[0] * 0.5}px`,
                      },
                    }}
                  >
                    <SurveyTemplate
                      methods={methods}
                      checkDisabled={checkDisabled}
                      type={ESurveyTypes.Customer}
                      handleSkip={handleSkip}
                      handleNext={methods.handleSubmit(handleNext)}
                      questionData={questionData}
                      handlePrev={handlePrev}
                    />
                  </Box>
                </Slide>
              </CardContent>
            </Card>
          </Box>
        </ThemeProvider>
      ) : (
        <Box alignItems={"center"} display={"flex"}>
          <Card
            sx={{
              height: {
                xs: `${windowSize.current[1] * 0.95}px`,
                sm: `${windowSize.current[1] * 0.8}px`,
              },
              backgroundColor: "rgb(255 255 255 / 97%)",
            }}
          >
            <CardContent sx={{ height: "100%", padding: { xs: 1 } }}>
              <Box
                sx={{
                  width: {
                    xs: `${windowSize.current[0] * 0.95}px`,
                    sm: `${windowSize.current[0] * 0.6}px`,
                    md: `${windowSize.current[0] * 0.6}px`,
                  },
                  height: "100%",
                }}
                display="flex"
                justifyContent={"center"}
                alignItems={"center"}
              >
                {status ? (
                  <Box textAlign="center">
                    <img
                      src={config?.isFinished ? Completed : Not_available}
                      alt="status"
                    />
                    <Typography fontSize={20} pt={5}>
                      {status}
                    </Typography>
                  </Box>
                ) : (
                  <CircularProgress />
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default SurveyPreview;
